import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Brand } from '../components/Brand';
import { ThemeToggle } from '../components/ThemeToggle';
import { PasswordField } from '../components/PasswordField';

interface Described {
  email: string;
  name: string;
  purpose: 'reset' | 'invite';
}

/**
 * Where a reset or an invite link lands. The link is checked before anything is asked, so a
 * stale one says so immediately rather than after somebody has typed a password twice.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();

  const [account, setAccount] = useState<Described | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setLinkError('That link is missing its code. Ask for a new one.');
      return;
    }
    api
      .get<Described>(`/auth/reset-password?token=${encodeURIComponent(token)}`)
      .then(setAccount)
      .catch((err) => setLinkError(err instanceof ApiError ? err.message : 'That link could not be checked.'));
  }, [token]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
      // Straight to sign in, since the reset ends any session that was already open.
      window.setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not set that password.');
    } finally {
      setBusy(false);
    }
  }

  const inviting = account?.purpose === 'invite';

  return (
    <div className="login-wrap">
      <section className="login-pitch">
        <Brand large />
        <div className="login-pitch-body">
          <div className="eyebrow">Lucentra Systems · Egen Holdings</div>
          <h1>
            {inviting ? (
              <>
                Welcome to <em>the Hub</em>.
              </>
            ) : (
              <>
                Choose a <em>new password</em>.
              </>
            )}
          </h1>
          <p className="subtitle" style={{ marginTop: 20, maxWidth: '46ch' }}>
            {inviting
              ? 'Set a password and you are in. Nobody else ever sees it, including whoever created your account.'
              : 'Once you set it, any session open anywhere else is signed out.'}
          </p>
        </div>
        <p className="login-pitch-foot">Access is granted by an administrator. There is no public sign-up.</p>
      </section>

      <section className="login-form-side">
        <div className="login-card">
          <div className="row-between mb-2">
            <div className="eyebrow" style={{ margin: 0 }}>
              {inviting ? 'Set your password' : 'New password'}
            </div>
            <ThemeToggle compact />
          </div>

          {linkError ? (
            <>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>This link cannot be used</h2>
              <div className="callout" role="alert">
                {linkError}
              </div>
              <div className="row mt-2">
                <Link to="/forgot-password" className="btn btn-primary">
                  Ask for a new link
                </Link>
                <Link to="/login" className="btn btn-quiet">
                  Back to sign in
                </Link>
              </div>
            </>
          ) : done ? (
            <>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>Password set</h2>
              <div className="callout callout-ok">
                You can sign in with it now. Taking you to the sign-in page…
              </div>
              <Link to="/login" className="btn btn-primary mt-2">
                Sign in
              </Link>
            </>
          ) : !account ? (
            <div className="empty">Checking your link…</div>
          ) : (
            <>
              <h2 style={{ fontSize: 24, marginBottom: 6 }}>
                {inviting ? `Hello ${account.name.split(' ')[0]}` : 'Choose a new password'}
              </h2>
              <p className="subtitle mb-2">
                For <strong>{account.email}</strong>.
              </p>

              {error && (
                <div className="callout" role="alert">
                  {error}
                </div>
              )}

              <form onSubmit={submit} noValidate>
                <label className="field">
                  <span className="field-label">New password</span>
                  <PasswordField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                  <span className="field-hint">
                    At least 10 characters, using 3 of: lowercase, uppercase, digit, symbol.
                  </span>
                </label>

                <label className="field">
                  <span className="field-label">Confirm it</span>
                  <PasswordField
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </label>

                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ width: '100%', justifyContent: 'center' }}
                  disabled={busy || !password || !confirm}
                >
                  {busy ? 'Saving…' : inviting ? 'Set my password and sign in' : 'Set new password'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
